/* eslint-disable react/jsx-curly-newline */
/* eslint-disable react/destructuring-assignment */
import React, { useState, useContext } from 'react';
import './styles.css';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { UserContext } from '../../../providers/UserProvider';

const SalesCenterManualFirebaseSettings = ({ salesCenter, onCompletedManualFirebaseSteps }) => {
  const userContext = useContext(UserContext);

  const manualPrereqSteps = [
    [
      'Upgrade To Blaze Plan',
      `https://console.firebase.google.com/project/${salesCenter.firebaseProjectId}/overview`,
      '/images/setup/firebaseUpgrade.png',
    ],
    [
      'Enable Storage (select US-CENTRAL location)',
      `https://console.firebase.google.com/project/${salesCenter.firebaseProjectId}/storage`,
      '/images/setup/firebaseStorage.png',
    ],
  ];

  const automatedSteps = [
    'Create Firestore Database',
    'Create Real Time Database',
    'Add Auth Providers',
    'Add Auth Domain',
    'Deploy Storage Rules',
    'Set Storage CORS Policy',
    'Set IAM Permissions',
  ];

  const manualPostSteps = [
    [
      'Enable Google Analytics',
      `https://console.firebase.google.com/project/${salesCenter.firebaseProjectId}/settings/integrations`,
      '/images/setup/firebaseAnalytics.png',
    ],
  ];

  const existingStatus = salesCenter.firebaseServicesSetup || {};
  const alreadyComplete = existingStatus.status === 'COMPLETE';
  const existingManualSteps = existingStatus.manualSteps || [];

  const [prereqsDone, setPrereqsDone] = useState(alreadyComplete);
  const [currentPrereqStep, setCurrentPrereqStep] = useState(0);

  const [automationStatus, setAutomationStatus] = useState(
    alreadyComplete ? 'COMPLETE' : 'IDLE',
  );
  const [automationMessage, setAutomationMessage] = useState(
    alreadyComplete ? 'All automated steps complete' : '',
  );
  const [automationError, setAutomationError] = useState(
    existingStatus.error ? existingStatus.message : '',
  );

  const [currentPostStep, setCurrentPostStep] = useState(0);

  const extraManualSteps = existingManualSteps.map((s) => [
    s.label,
    s.url,
    null,
  ]);
  const allPostSteps = [...extraManualSteps, ...manualPostSteps];

  const runSetup = () => {
    setAutomationStatus('IN-PROGRESS');
    setAutomationMessage('Starting automated Firebase setup...');
    setAutomationError('');

    const timeout = setTimeout(() => {
      setAutomationStatus('ERROR');
      setAutomationError(
        'Setup timed out. The server may still be processing.'
        + ' Try refreshing or click Retry.',
      );
    }, 120000);

    userContext.socket.emit(
      'setupFirebaseServices',
      salesCenter.subdomain,
      (status, message) => {
        clearTimeout(timeout);
        if (status === 'COMPLETE') {
          setAutomationStatus('COMPLETE');
          setAutomationMessage('All automated steps complete');
          setAutomationError('');
        } else if (status === 'ERROR') {
          setAutomationStatus('ERROR');
          setAutomationError(message || 'Unknown error');
        } else if (status === 'IN-PROGRESS') {
          setAutomationStatus('IN-PROGRESS');
          setAutomationMessage(message || 'Setting up Firebase services...');
        }
      },
    );
  };

  const handlePrereqDone = () => {
    if (currentPrereqStep + 1 < manualPrereqSteps.length) {
      setCurrentPrereqStep((prev) => prev + 1);
      return;
    }
    setPrereqsDone(true);
    runSetup();
  };

  const handlePostStepDone = () => {
    if (currentPostStep + 1 < allPostSteps.length) {
      setCurrentPostStep((prev) => prev + 1);
    } else {
      onCompletedManualFirebaseSteps();
    }
  };

  const retryAutomation = () => {
    runSetup();
  };

  const automationComplete = automationStatus === 'COMPLETE';
  const activePrereqStep = manualPrereqSteps[currentPrereqStep];
  const activePostStep = allPostSteps[currentPostStep];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

      {/* PHASE 1: Manual Prerequisites */}
      <div
        style={{
          padding: '20px',
          border: '1px solid #ddd',
          borderRadius: '8px',
          margin: '0 20px',
        }}
      >
        <h4 style={{ marginTop: 0 }}>
          Step 1: Manual Prerequisites
        </h4>
        {
          prereqsDone ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: 'green',
              }}
            >
              <CheckCircleIcon />
              <span>Prerequisites complete</span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '400px',
                  paddingLeft: '20px',
                }}
              >
                <p style={{ color: '#666', fontSize: '14px', marginTop: 0 }}>
                  Complete these steps in the Firebase Console
                  before automated setup can run.
                </p>
                {
                  manualPrereqSteps.map((step, index) => (
                    <div
                      key={step[0]}
                      style={{
                        width: '350px',
                        minHeight: '40px',
                        borderBottom: '1px dotted black',
                        display: 'flex',
                        padding: '4px 0',
                      }}
                    >
                      <div
                        style={{
                          width: '30px',
                          fontWeight: 'bold',
                          margin: 'auto 0',
                        }}
                      >
                        {index + 1}
                      </div>
                      <div
                        style={{
                          width: '220px',
                          margin: 'auto 0',
                        }}
                      >
                        <a
                          href={step[1]}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {step[0]}
                        </a>
                      </div>
                      <div>
                        {
                          index < currentPrereqStep && (
                            <CheckCircleIcon
                              style={{ color: 'green' }}
                            />
                          )
                        }
                        {
                          index === currentPrereqStep && (
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={handlePrereqDone}
                            >
                              DONE
                            </Button>
                          )
                        }
                      </div>
                    </div>
                  ))
                }
              </div>
              {
                activePrereqStep[2] && (
                  <div
                    style={{
                      margin: '30px',
                      border: '3px solid red',
                      borderRadius: '20px',
                      padding: '10px',
                      textAlign: 'center',
                      flexGrow: '1',
                      maxWidth: '700px',
                    }}
                  >
                    <img
                      src={
                        process.env.PUBLIC_URL
                        + activePrereqStep[2]
                      }
                      alt="setup step"
                      style={{
                        height: '300px',
                        objectFit: 'contain',
                        marginBottom: '25px',
                      }}
                    />
                  </div>
                )
              }
            </div>
          )
        }
      </div>

      {/* PHASE 2: Automated Setup (only after prereqs) */}
      {
        prereqsDone && (
          <div
            style={{
              padding: '20px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              margin: '0 20px',
            }}
          >
            <h4 style={{ marginTop: 0 }}>
              Step 2: Automated Setup
            </h4>
            {
              automatedSteps.map((step, index) => (
                <div
                  key={step}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: '1px dotted #ccc',
                    gap: '10px',
                  }}
                >
                  <div
                    style={{ width: '30px', fontWeight: 'bold' }}
                  >
                    {index + 1}
                  </div>
                  <div style={{ flexGrow: 1 }}>{step}</div>
                  <div>
                    {
                      automationComplete && (
                        <CheckCircleIcon
                          style={{ color: 'green' }}
                        />
                      )
                    }
                    {
                      automationStatus === 'IN-PROGRESS' && (
                        <CircularProgress size={20} />
                      )
                    }
                    {
                      automationStatus === 'ERROR' && (
                        <ErrorIcon style={{ color: 'red' }} />
                      )
                    }
                  </div>
                </div>
              ))
            }
            {
              automationStatus === 'IN-PROGRESS' && (
                <div
                  style={{
                    marginTop: '10px',
                    color: '#666',
                    fontSize: '14px',
                  }}
                >
                  {automationMessage}
                </div>
              )
            }
            {
              automationStatus === 'ERROR' && (
                <div style={{ marginTop: '10px' }}>
                  <div
                    style={{
                      color: 'red',
                      fontSize: '14px',
                      marginBottom: '10px',
                    }}
                  >
                    Error: {automationError}
                  </div>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={retryAutomation}
                  >
                    Retry
                  </Button>
                </div>
              )
            }
            {
              automationComplete
              && extraManualSteps.length > 0 && (
                <div
                  style={{
                    marginTop: '10px',
                    padding: '10px',
                    backgroundColor: '#fff3e0',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <WarningIcon style={{ color: '#e65100' }} />
                  <span
                    style={{
                      fontSize: '14px',
                      color: '#e65100',
                    }}
                  >
                    {extraManualSteps.length}
                    {' '}step(s) could not be automated
                    {' '}and have been added to the final
                    {' '}steps below.
                  </span>
                </div>
              )
            }
          </div>
        )
      }

      {/* PHASE 3: Post-setup manual steps (only after automation) */}
      {
        automationComplete && (
          <div
            style={{
              padding: '20px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              margin: '0 20px',
            }}
          >
            <h4 style={{ marginTop: 0 }}>
              Step 3: Final Setup
            </h4>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '400px',
                  paddingLeft: '20px',
                }}
              >
                {
                  allPostSteps.map((step, index) => (
                    <div
                      key={step[0]}
                      style={{
                        width: '350px',
                        minHeight: '40px',
                        borderBottom: '1px dotted black',
                        display: 'flex',
                        padding: '4px 0',
                      }}
                    >
                      <div
                        style={{
                          width: '30px',
                          fontWeight: 'bold',
                          margin: 'auto 0',
                        }}
                      >
                        {index + 1}
                      </div>
                      <div
                        style={{
                          width: '220px',
                          margin: 'auto 0',
                        }}
                      >
                        <a
                          href={step[1]}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {step[0]}
                        </a>
                      </div>
                      <div>
                        {
                          index < currentPostStep && (
                            <CheckCircleIcon
                              style={{ color: 'green' }}
                            />
                          )
                        }
                        {
                          index === currentPostStep && (
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={handlePostStepDone}
                            >
                              DONE
                            </Button>
                          )
                        }
                      </div>
                    </div>
                  ))
                }
              </div>
              {
                activePostStep
                && activePostStep[2] && (
                  <div
                    style={{
                      margin: '30px',
                      border: '3px solid red',
                      borderRadius: '20px',
                      padding: '10px',
                      textAlign: 'center',
                      flexGrow: '1',
                      maxWidth: '700px',
                    }}
                  >
                    <img
                      src={
                        process.env.PUBLIC_URL
                        + activePostStep[2]
                      }
                      alt="setup step"
                      style={{
                        height: '300px',
                        objectFit: 'contain',
                        marginBottom: '25px',
                      }}
                    />
                  </div>
                )
              }
            </div>
          </div>
        )
      }
    </div>
  );
};

export default SalesCenterManualFirebaseSettings;
